import React, { useEffect, useState } from 'react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { EditableList } from './ui'
import { API_BASE_URL } from '../data'
import { JVPoint } from '../data/types'
import { StarIcon } from '../icons'

type Props = {
    isOpen: boolean
    onClose: () => void
    volunteerId: number
    initialEntry?: JVPoint | null
    onSaved: () => void
}

type DutyEntry = {
    _id: string
    name: string
}

export const POINT_FIELDS: { key: string; label: string; max: number }[] = [
    { key: 'cleanup_point', label: 'Cleanup', max: 10 },
    { key: 'front_duty_point', label: 'Front Duty', max: 10 },
    { key: 'ghatpaat_point', label: 'Ghatpaat', max: 10 },
    { key: 'standing_duty_point', label: 'Standing Duty', max: 10 },
    { key: 'ushering_point', label: 'Ushering', max: 10 },
    { key: 'nandi_helper_point', label: 'Nandi Helper', max: 10 },
    { key: 'jura_niaz_distribution_point', label: 'Jura Niaz Distribution', max: 10 },
    { key: 'shoe_cloakroom_point', label: 'Shoe Cloakroom', max: 10 },
    { key: 'crowd_management_point', label: 'Crowd Management', max: 10 },
    { key: 'social_hall_refreshment_point', label: 'Social Hall Refreshment', max: 10 },
    { key: 'full_uniform_point', label: 'Full Uniform', max: 5 },
    { key: 'institutional_support_work_point', label: 'Institutional Support Work', max: 20 },
    { key: 'complete_dofe_point', label: 'Complete DofE', max: 30 },
    { key: 'recognized_award_point', label: 'Recognized Award', max: 30 },
    { key: 'school_award_point', label: 'School Award', max: 30 },
]

export const DUTY_LISTS: { key: 'other_internal_duty_points' | 'other_external_duty_points' | 'iv_exec_approved_duty_points'; label: string; max: number }[] = [
    { key: 'other_internal_duty_points', label: 'Other Internal Duty Points', max: 10 },
    { key: 'other_external_duty_points', label: 'Other External Duty Points', max: 20 },
    { key: 'iv_exec_approved_duty_points', label: 'IV Exec Approved Duty Points', max: 50 },
]

// The backend's JVPoint::total_points is an accessor, not an appended attribute,
// so it isn't present on entries returned by the list/get endpoints. Compute it
// client-side from the same fields the accessor itself sums.
export const computeTotalPoints = (entry: JVPoint): number => {
    let total = 0

    POINT_FIELDS.forEach(({ key }) => {
        const value = entry[key as keyof JVPoint] as number | null | undefined
        total += value ?? 0
    })

    DUTY_LISTS.forEach(({ key }) => {
        const items = (entry[key] ?? []) as { name: string; points: number }[]
        items.forEach(item => { total += item.points ?? 0 })
    })

    return total
}

const createEmptyDutyEntry = (): DutyEntry => ({
    _id: `duty-${Date.now()}-${Math.random()}`,
    name: ''
})

const emptyPointChecks = (): Record<string, boolean> =>
    POINT_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: false }), {} as Record<string, boolean>)

type DutyEntryListProps = {
    label: string
    items: DutyEntry[]
    max: number
    onChange: (items: DutyEntry[]) => void
}

const DutyEntryList = ({ label, items, max, onChange }: DutyEntryListProps) => {
    const updateItem = (id: string, value: string) => {
        onChange(items.map(item => item._id === id ? { ...item, name: value } : item))
    }

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label} ({max} pts each)</label>
            <EditableList
                items={items}
                onItemsChange={onChange}
                getItemId={item => item._id}
                createNewItem={createEmptyDutyEntry}
                addButtonLabel="Add Entry"
                emptyMessage="No entries yet"
                itemLabel="Duty"
                renderItem={(item) => (
                    <input
                        value={item.name}
                        onChange={e => updateItem(item._id, e.target.value)}
                        placeholder="Name"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 mt-3 dark:bg-gray-900 dark:text-gray-100"
                    />
                )}
            />
        </div>
    )
}

const dutyEntriesFromModel = (entries: { name: string; points: number }[] | null | undefined): DutyEntry[] =>
    (entries ?? []).map(({ name }) => ({
        _id: `duty-${Date.now()}-${Math.random()}`,
        name
    }))

export const AddJVPointModal = ({ isOpen, onClose, volunteerId, initialEntry, onSaved }: Props) => {
    const isEditMode = !!initialEntry

    const [dateOfEntry, setDateOfEntry] = useState('')
    const [pointChecks, setPointChecks] = useState<Record<string, boolean>>(emptyPointChecks())
    const [dutyEntries, setDutyEntries] = useState<Record<string, DutyEntry[]>>({
        other_internal_duty_points: [],
        other_external_duty_points: [],
        iv_exec_approved_duty_points: []
    })

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (isOpen) {
            if (initialEntry) {
                setDateOfEntry(initialEntry.date_of_entry.slice(0, 10))
                setPointChecks(
                    POINT_FIELDS.reduce((acc, { key }) => {
                        const value = initialEntry[key as keyof JVPoint] as number | null | undefined
                        return { ...acc, [key]: !!value && value > 0 }
                    }, {} as Record<string, boolean>)
                )
                setDutyEntries({
                    other_internal_duty_points: dutyEntriesFromModel(initialEntry.other_internal_duty_points),
                    other_external_duty_points: dutyEntriesFromModel(initialEntry.other_external_duty_points),
                    iv_exec_approved_duty_points: dutyEntriesFromModel(initialEntry.iv_exec_approved_duty_points)
                })
            } else {
                setDateOfEntry('')
                setPointChecks(emptyPointChecks())
                setDutyEntries({
                    other_internal_duty_points: [],
                    other_external_duty_points: [],
                    iv_exec_approved_duty_points: []
                })
            }
            setFieldErrors({})
            setFormError(null)
        }
    }, [isOpen, initialEntry])

    const validate = (): boolean => {
        const errors: Record<string, string> = {}

        if (!dateOfEntry) {
            errors.date_of_entry = 'Date is required'
        }

        let dutyListError: string | null = null
        for (const { key, label } of DUTY_LISTS) {
            for (const entry of dutyEntries[key]) {
                if (!entry.name.trim()) {
                    dutyListError = `${label}: each entry needs a name`
                    break
                }
            }
            if (dutyListError) break
        }

        setFieldErrors(errors)
        setFormError(dutyListError)

        return Object.keys(errors).length === 0 && !dutyListError
    }

    const handleClose = () => {
        if (submitting) return
        onClose()
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setFormError(null)

        if (!validate()) return

        setSubmitting(true)
        try {
            const payload: Record<string, unknown> = {
                volunteer_csid: volunteerId,
                date_of_entry: dateOfEntry,
            }

            POINT_FIELDS.forEach(({ key, max }) => {
                payload[key] = pointChecks[key] ? max : null
            })

            DUTY_LISTS.forEach(({ key, max }) => {
                payload[key] = dutyEntries[key].map(({ name }) => ({
                    name: name.trim(),
                    points: max
                }))
            })

            const url = isEditMode
                ? `${API_BASE_URL}/jv-point/${initialEntry!.id}`
                : `${API_BASE_URL}/jv-point`

            const response = await fetch(url, {
                method: isEditMode ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload)
            })

            if (!response.ok) {
                const body = await response.json().catch(() => null)

                if (response.status === 422 && body?.errors) {
                    const serverFieldErrors: Record<string, string> = {}
                    Object.entries(body.errors as Record<string, string[]>).forEach(([key, messages]) => {
                        const topLevelKey = key.split('.')[0]
                        if (!serverFieldErrors[topLevelKey]) {
                            serverFieldErrors[topLevelKey] = Array.isArray(messages) ? messages[0] : String(messages)
                        }
                    })
                    setFieldErrors(prev => ({ ...prev, ...serverFieldErrors }))
                    setFormError('Please fix the highlighted fields and try again.')
                } else {
                    setFormError(body?.message || 'Failed to save point entry.')
                }
                return
            }

            onSaved()
            onClose()
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to save point entry.')
        } finally {
            setSubmitting(false)
        }
    }

    const liveTotal = POINT_FIELDS.reduce((sum, { key, max }) => sum + (pointChecks[key] ? max : 0), 0)
        + DUTY_LISTS.reduce((sum, { key, max }) => sum + dutyEntries[key].length * max, 0)

    const footer = (
        <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" form="add-jv-point-form" disabled={submitting}>
                {submitting ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save')}
            </Button>
        </div>
    )

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={isEditMode ? 'Edit Point Record' : 'Add Point Record'} footer={footer} maxWidth="2xl">
            <form id="add-jv-point-form" onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
                {formError && (
                    <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded-lg text-sm">
                        {formError}
                    </div>
                )}

                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-2">
                    <StarIcon className="w-4 h-4" />
                    Total Points: {liveTotal}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Entry</label>
                    <input
                        type="date"
                        value={dateOfEntry}
                        onChange={e => setDateOfEntry(e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-900 dark:text-gray-100"
                    />
                    {fieldErrors.date_of_entry && (
                        <p className="text-danger-600 dark:text-danger-400 text-xs mt-1">{fieldErrors.date_of_entry}</p>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {POINT_FIELDS.map(({ key, label, max }) => (
                        <div key={key}>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={pointChecks[key] ?? false}
                                    onChange={e => setPointChecks(prev => ({ ...prev, [key]: e.target.checked }))}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">{label} ({max} pts)</span>
                            </label>
                            {fieldErrors[key] && (
                                <p className="text-danger-600 dark:text-danger-400 text-xs mt-1">{fieldErrors[key]}</p>
                            )}
                        </div>
                    ))}
                </div>

                {DUTY_LISTS.map(({ key, label, max }) => (
                    <DutyEntryList
                        key={key}
                        label={label}
                        max={max}
                        items={dutyEntries[key]}
                        onChange={(items) => setDutyEntries(prev => ({ ...prev, [key]: items }))}
                    />
                ))}
            </form>
        </Modal>
    )
}
